import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { verifyStancerPayment } from '@/features/checkout/stancerClient';
import { readOrderAccessToken } from '@/features/checkout/ordersApi';
import { clearRestaurantCart } from '@/features/cart/cartSlice';
import type { RootState } from '@/app/store';

export default function PaymentReturnPage() {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const consumerToken = useSelector((root: RootState) => root.user.token);
  const { t } = useTranslation();
  const [state, setState] = useState<'verifying' | 'paid' | 'pending' | 'failed'>('verifying');
  const clearedRef = useRef(false);
  // SC-542: kept so a failed payment can send the customer back to the right
  // restaurant's menu. The cart is only cleared on 'paid', so their items are
  // still there and they can simply order again.
  const [restaurantUuid, setRestaurantUuid] = useState('');

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let navigationTimer: ReturnType<typeof setTimeout> | undefined;

    async function run() {
      try {
        const r = await verifyStancerPayment(uuid, {
          orderAccessToken: readOrderAccessToken(uuid),
          consumerToken,
        });
        if (cancelled) return;
        if (r.restaurant_uuid) setRestaurantUuid(r.restaurant_uuid);
        if (r.status === 'paid') {
          setState('paid');
          if (r.restaurant_uuid && !clearedRef.current) {
            dispatch(clearRestaurantCart({ restaurantUuid: r.restaurant_uuid }));
            clearedRef.current = true;
          }
          const target = r.restaurant_uuid
            ? `/${r.restaurant_uuid}/confirmation/${uuid}`
            : `/`;
          navigationTimer = setTimeout(() => navigate(target), 1200);
        } else if (r.status === 'pending') {
          setState('pending');
          retryTimer = setTimeout(run, 3000);
        } else {
          setState('failed');
        }
      } catch {
        if (!cancelled) setState('failed');
      }
    }

    run();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (navigationTimer) clearTimeout(navigationTimer);
    };
  }, [uuid, navigate, dispatch, consumerToken]);

  return (
    <div className="payment-return max-w-[600px] mx-auto px-4 py-12 text-center font-mono">
      {state === 'verifying' && (
        <p className="text-ink/70">{t('checkout.return.verifying', 'Vérification du paiement…')}</p>
      )}
      {state === 'paid' && (
        <p className="text-green-700">{t('checkout.return.paid', 'Paiement confirmé')}</p>
      )}
      {state === 'pending' && (
        <p className="text-ink/70">{t('checkout.return.pending', 'Paiement en cours de validation…')}</p>
      )}
      {state === 'failed' && (
        <>
          <p className="text-sale-red">{t('checkout.return.failed', 'Paiement non finalisé')}</p>
          {/* SC-542: without this the customer was stranded on a dead page — the
              payment failed, nothing was ordered, and there was no way back.
              Their cart was never cleared, so this returns them to the menu
              with their items intact and they can pay again. */}
          <p className="mt-2 text-ink/70">
            {t('checkout.return.failed_hint', "Votre commande n'a pas été validée. Votre panier a été conservé.")}
          </p>
          <button
            type="button"
            onClick={() => navigate(restaurantUuid ? `/${restaurantUuid}` : '/')}
            className="mt-6 underline"
          >
            {t('checkout.return.back_to_order', 'Retourner à ma commande')}
          </button>
        </>
      )}
    </div>
  );
}
